import { Col, Row, message } from "antd";
import { useEffect, useRef, useState } from "react";
import { useQuote } from "../QuoteProvider";
import QuoteButton from "../components/QuoteButton/QuoteButton";
import QuotePersonalDetailsForm, {
  type QuoteContactFormField,
  type QuotePersonalDetailsFormHandle,
} from "../components/QuotePersonalDetailsForm";
import QuotePrivacyPolicy, {
  TERMS_AGREEMENT_ANCHOR_ID,
} from "../components/QuotePrivacyPolicy";
import QuoteSummary from "../QuoteSummary";
import QuoteAppPreview from "../components/QuoteAppPreview/QuoteAppPreview";
import ComparisonTable from "../components/ComparisonTable/ComparisonTable";
import { useGAEventLogger } from "../../hooks/useGAEventLogger";
import {
  useQuoteFormValidation,
  CONTACT_FIELD_LABEL_KEYS,
  type MissingContactField,
} from "../hooks/useQuoteFormValidation";
import { trackQuoteContactCtaDisabledClicked } from "../../../utils/quoteAnalytics";
import { EMAIL_UNDELIVERABLE_MESSAGE } from "../../utils/emailValidationCore";
import {
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { useTranslationNamespace } from "../../../hooks/useTranslationNamespace";
import { useIsMobile } from "../MobileBreakpointContext";
import { loadFunnelRecaptcha } from "../../../utils/funnelRecaptcha";
import "./scss/step-contact.scss";

type StepContactProps = {
  next: (override?: boolean) => void;
};

const iconCheck = (
  <CheckCircleOutlined style={{ color: "var(--color-success)", fontSize: 28 }} />
);
const iconWarn = (
  <WarningOutlined style={{ color: "var(--color-warning)", fontSize: 28 }} />
);
const iconX = (
  <CloseCircleOutlined style={{ color: "var(--color-danger)", fontSize: 28 }} />
);

// Comparison table data (complete)
const comparisonTableData = [
  {
    feature: "Average Price per Procedure",
    budgetFacility: "$5,000",
    midTierPractice: "$15,000",
    celebrityPractice: "$25,000",
    topAesthetics: "$8,000",
  },
  {
    feature: "Board-Certified Plastic Surgeon",
    budgetFacility: "Varies",
    midTierPractice: "Varies",
    celebrityPractice: iconCheck,
    topAesthetics: iconCheck,
  },
  {
    feature: "Years of Experience",
    budgetFacility: "Varies",
    midTierPractice: "~6 Years",
    celebrityPractice: "10+ Years",
    topAesthetics: "10+ Years",
  },
  {
    feature: "Surgeon Owned and Managed Surgery Center",
    budgetFacility: iconWarn,
    midTierPractice: "Varies",
    celebrityPractice: "Varies",
    topAesthetics: iconCheck,
  },
  {
    feature: "Dedicated Operating Surgeon",
    budgetFacility: iconWarn,
    midTierPractice: iconCheck,
    celebrityPractice: iconCheck,
    topAesthetics: iconCheck,
  },
  {
    feature: "Complete MedSpa Services",
    budgetFacility: iconWarn,
    midTierPractice: "Varies",
    celebrityPractice: iconCheck,
    topAesthetics: iconCheck,
  },
  {
    feature: "Dedicated Anesthesia Provider",
    budgetFacility: iconWarn,
    midTierPractice: iconWarn,
    celebrityPractice: iconCheck,
    topAesthetics: iconCheck,
  },
  {
    feature: "Complete Recovery Coordination",
    budgetFacility: iconX,
    midTierPractice: iconX,
    celebrityPractice: iconX,
    topAesthetics: iconCheck,
  },
  {
    feature: "Transportation Assistance",
    budgetFacility: iconX,
    midTierPractice: iconX,
    celebrityPractice: iconX,
    topAesthetics: iconCheck,
  },
  {
    feature: "Advanced Pre- & Post-Op Safety Protocols",
    budgetFacility: "Limited",
    midTierPractice: "Standard",
    celebrityPractice: "Standard",
    topAesthetics: "Comprehensive",
  },
  {
    feature: "Financing Options",
    budgetFacility: "Varies",
    midTierPractice: "Varies",
    celebrityPractice: "Varies",
    topAesthetics: "Yes - Multiple Options",
  },
  {
    feature: "Patient Satisfaction (Avg Google Rating)",
    budgetFacility: "3.8",
    midTierPractice: "4.1",
    celebrityPractice: "4.7",
    topAesthetics: "4.9",
  },
];

const InfoCard = ({
  heading,
  body,
}: {
  heading: string;
  body: string;
}) => (
  <div className="step-contact__info-card">
    <ExclamationCircleOutlined className="step-contact__info-card-icon" />
    <div className="step-contact__info-card-text">
      <strong>{heading}</strong> {body}
    </div>
  </div>
);

// Missing-field key → the antd Form.Item that holds it. `hipaaAgreement` is
// absent on purpose: the Terms switch lives outside the details form and is
// scrolled to by DOM id instead.
const FIELD_TO_FORM_ITEM: Partial<
  Record<MissingContactField, QuoteContactFormField>
> = {
  name: "name",
  lastName: "lastname",
  email: "email",
  phone: "phone",
  phoneFormat: "phone",
  zipCode: "zipCode",
};

const StepContact = ({ next }: StepContactProps) => {
  const { logEvent } = useGAEventLogger();
  const quoteData = useQuote();
  const isMobile = useIsMobile();
  const { t } = useTranslationNamespace("quote");

  const { missingUserContactFields } = useQuoteFormValidation();
  // Disabled-CTA feedback (2026-09-13 usability fix), mirroring the procedures
  // step's hint from #2252. The contact step is the one that creates the lead,
  // so a tap that does nothing is the most expensive dead tap in the funnel.
  // Gated on the missing set below, so the hint clears itself the moment the
  // last field lands — no timers.
  const [showDisabledHint, setShowDisabledHint] = useState(false);
  const detailsFormRef = useRef<QuotePersonalDetailsFormHandle>(null);
  // Hard-block on a server-confirmed definitive-negative email (owner
  // decision, 2026-08-23 — see QuotePersonalDetailsForm's onBlur check
  // against validateEmailInput). Cleared as soon as the field is edited
  // again, re-set only once the next blur re-confirms invalid.
  const [emailBlocked, setEmailBlocked] = useState(false);

  // Bot-score Phase 1 (2026-09-13): pull reCAPTCHA v3's api.js in HERE, not on
  // funnel entry. This is the first step that can create a lead, and the
  // funnel is mobile-first + LCP-sensitive — loading Google's script on the
  // procedure picker would cost every visitor for a check only submitters
  // need. Idempotent and fail-open: a blocked script just means no token.
  useEffect(() => {
    loadFunnelRecaptcha();
  }, []);

  // contact-sweeps-v1 RETIRED 2026-06-20 — "no_sweeps" won, so the "$2,000 gift
  // card / promotional voucher" framing is removed for EVERYONE; the ask is
  // framed around the free evaluation itself.
  const hideSweeps = true;
  const heading = hideSweeps ? t("contact.headingClean") : t("contact.heading");
  const infoBody = hideSweeps
    ? t("contact.unlockSubtextClean")
    : t("contact.giftCard");

  const ctaDisabled = missingUserContactFields.length > 0 || emailBlocked;

  const handleDisabledCtaClick = () => {
    // Field NAMES only — never the values the patient typed (see the tracker's
    // NO PII note).
    trackQuoteContactCtaDisabledClicked(missingUserContactFields);

    // emailBlocked is a server verdict on a filled-in field, not a gap in the
    // form, so it gets its own message rather than a "still needed" list.
    if (missingUserContactFields.length === 0 && emailBlocked) {
      message.error(EMAIL_UNDELIVERABLE_MESSAGE);
      return;
    }

    setShowDisabledHint(true);

    const firstMissing = missingUserContactFields[0];
    if (!firstMissing) return;
    const formItem = FIELD_TO_FORM_ITEM[firstMissing];
    if (formItem) {
      detailsFormRef.current?.focusField(formItem);
      return;
    }
    document
      .getElementById(TERMS_AGREEMENT_ANCHOR_ID)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <div className="step-contact">
      {!isMobile && (
        <h1 className="step-contact__heading">{heading}</h1>
      )}

      {isMobile && (
        <div className="step-contact__panel">
          <InfoCard heading={t("contact.unlockQuote")} body={infoBody} />
        </div>
      )}

      <Row>
        <Col
          span={isMobile ? 24 : 11}
          offset={!isMobile ? 1 : 0}
          className="step-contact__panel"
        >
          <QuotePersonalDetailsForm
            ref={detailsFormRef}
            onEmailBlockedChange={setEmailBlocked}
          />
          <QuotePrivacyPolicy />
          <div className="quote-button-mobile-container">
            {showDisabledHint && missingUserContactFields.length > 0 && (
              <div className="step-contact__cta-hint" role="status">
                {t("contact.ctaDisabledHint", {
                  fields: missingUserContactFields
                    .map((field) => t(CONTACT_FIELD_LABEL_KEYS[field]))
                    .join(", "),
                })}
              </div>
            )}
            <QuoteButton
              disabled={ctaDisabled}
              onDisabledClick={handleDisabledCtaClick}
              onClick={() => {
                if (!quoteData.hipaaAgreement) {
                  message.error(t("contact.hipaaError"));
                  return;
                }
                if (emailBlocked) {
                  message.error(EMAIL_UNDELIVERABLE_MESSAGE);
                  return;
                }
                quoteData.setSkippedPersonalDetails(false);

                logEvent("User", "Clicked Button", "StepContact: Next");
                next();
              }}
            >
              {t("contact.getInstantQuote")}
            </QuoteButton>
          </div>
        </Col>
        <Col span={isMobile ? 24 : 11} className="step-contact__panel--right">
          {!isMobile && (
            <InfoCard heading={t("contact.unlockQuote")} body={infoBody} />
          )}
          {(quoteData.quoteReloaded || quoteData.quoteShared) &&
          quoteData.selectedProcedures.length > 0 ? (
            <QuoteSummary />
          ) : (
            <QuoteAppPreview />
          )}
        </Col>
      </Row>

      <div className="step-contact__comparison-wrap">
        <ComparisonTable rows={comparisonTableData} />
      </div>

      {isMobile && <div className="step-contact__mobile-spacer" />}
    </div>
  );
};

export default StepContact;
